import { message } from 'antd';
import moment from 'moment-timezone';
import FetchUtils, { parseJSON } from './fetch_utils';
import { getBackendUri } from '../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../utils/DataConstants';
import { logoutSuccess } from '../actions/session_actions';
import {
	WORK_ORDERS_CRUD_ACTION_CREATORS,
	workOrderStarWritten,
} from '../actions/work_orders_actions';
import {
	currentUserEmail,
	getWorkOrderStarState,
	isWorkOrderRemindersEnabled,
	parseRemindAt,
	REMINDER_STATUS,
	WorkOrderUserStar,
} from '../data/workOrderStarLabels';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from './work_orders_thunks';
import { workOrderSearchAndAPIAdaptor } from './work_orders_thunks_wrapper';
import { ACTIVE_REMINDER_COUNT_COLLECTION } from '../constants/WorkOrderReminderCollections';
import { compareStrings, getEntityById, nullSafeGet } from '../utils/DataAccessUtils';
import { stopAllRinging } from '../hooks/useReminderRinging';
import { STAR_CHANGE_ACTION, WorkOrderStarChangeMessage } from '../hooks/useWorkOrderStarSocket';

const fetchUtils = new FetchUtils();

// Personal bookmark (star) on a work order with an optional short note and reminder.
//   PUT    /api/v1/{role}/work_order/work_orders/:woId/star   { starNote?, remindAt? }
//   DELETE /api/v1/{role}/work_order/work_orders/:woId/star
// The PUT is a full replacement: omitting remindAt clears the reminder, so a note-only edit
// must send the current remindAt back. Its response is the saved star, whose `action` says
// what changed (see STAR_ACTION).
const starUrl = (roleType: string, workOrderId: number | string): string =>
	`${getBackendUri()}/api/v1/${roleType}/work_order/work_orders/${workOrderId}/star`;

export const applyWorkOrderStar =
	(workOrderId: number | string, star: Partial<WorkOrderUserStar> | null) =>
	(dispatch, getState) => {
		const email = currentUserEmail();
		const workOrdersSlice = getState().work_orders;
		const searchRecord = (nullSafeGet('searchRecords', workOrdersSlice) || {})[workOrderId];
		const record = (nullSafeGet('records', workOrdersSlice) || {})[workOrderId];
		const searchStars = nullSafeGet('stars', searchRecord);
		const existing: WorkOrderUserStar[] =
			searchStars != null ? searchStars : nullSafeGet('stars', record) || [];
		// Case-insensitive match (see getWorkOrderStarState): casing differences between the
		// cached email and star.email must not leave a stale duplicate of the user's own star.
		const withoutMine = existing.filter(
			(existingStar) => existingStar && !compareStrings(existingStar.email, email)
		);
		const nextStars = star
			? [
					...withoutMine,
					{
						email,
						workOrderId,
						starNote: star.starNote || null,
						remindAt: star.remindAt || null,
						remindCondition: star.remindCondition || null,
					},
			  ]
			: withoutMine;
		dispatch(
			WORK_ORDERS_CRUD_ACTION_CREATORS.updatePartialSuccess({ id: workOrderId, stars: nextStars })
		);
	};

// How many of the user's reminders are due (a page-size-1 search's total), which the Tracked tab
// icon and the side menu's bell ring on. Re-read after every star write here, so every surface that
// tracks/snoozes/stops a reminder keeps it current; socket messages carry it (setActiveReminderCount).
// A count of 0 stops every ringing bell.
export const refreshActiveReminderCount = (roleType: string) => (dispatch, getState) => {
	if (!isWorkOrderRemindersEnabled()) return Promise.resolve();
	const thunks =
		roleType === ROLE_TYPES.SUPPLIER
			? workOrdersRestCrudThunksForSupplier
			: workOrdersRestCrudThunksForBuyer;
	const filters = {
		starredByCurrentUser: true,
		reminderStatusForCurrentUser: REMINDER_STATUS.ACTIVE,
	};
	return dispatch(
		thunks.searchWithPostMethod(
			filters,
			ACTIVE_REMINDER_COUNT_COLLECTION,
			{ current: 1, pageSize: 1 },
			null,
			filters,
			false,
			// Search hits arrive wrapped ({ source, highlight }); unwrap them like every work order search.
			workOrderSearchAndAPIAdaptor
		)
	)
		.then(() => {
			const total = nullSafeGet(
				'pagination.total',
				getState().work_orders[ACTIVE_REMINDER_COUNT_COLLECTION]
			);
			if (total === 0) stopAllRinging();
		})
		.catch(() => undefined);
};

// The due count from a `wo_star_change` message, which carries it on every change. With none due,
// every bell stops (see refreshActiveReminderCount too).
export const setActiveReminderCount = (count: number) => (dispatch) => {
	if (count === 0) stopAllRinging();
	return dispatch(
		WORK_ORDERS_CRUD_ACTION_CREATORS.updatePagination(
			{ total: count },
			ACTIVE_REMINDER_COUNT_COLLECTION
		)
	);
};

// A star change pushed over the WebSocket (from this tab, another tab, or another device): brings
// the cached work order's star in line with it. Only cached work orders are touched, and one that
// already matches (normally this tab's own write, applied from its PUT response) is left alone, so
// views that re-read on a star write (workOrderStarWritten) re-read only for changes made elsewhere.
export const applyWorkOrderStarChange =
	(message: WorkOrderStarChangeMessage) => (dispatch, getState) => {
		const workOrder = getEntityById(message.workOrderId, getState().work_orders, null);
		if (!workOrder) return;
		const star =
			message.action === STAR_CHANGE_ACTION.UNSTARRED
				? null
				: {
						starNote: message.starNote || null,
						remindAt: message.remindAt || null,
						remindCondition: message.remindCondition || null,
				  };
		const cached = getWorkOrderStarState(workOrder);
		const remindAt = star ? parseRemindAt(star.remindAt) : null;
		const matches = star
			? cached.starred &&
			  cached.note === star.starNote &&
			  cached.condition === star.remindCondition &&
			  (cached.remindAt ? cached.remindAt.valueOf() : null) ===
					(remindAt ? remindAt.valueOf() : null)
			: !cached.starred;
		if (matches) return;
		dispatch(applyWorkOrderStar(message.workOrderId, star));
		dispatch(workOrderStarWritten());
	};

const upsertWorkOrderStarForRole =
	(roleType: string) =>
	(
		workOrderId: number | string,
		starNote?: string | null,
		remindAt?: moment.Moment | null,
		remindCondition?: string | null
	) => {
		const data: { starNote: string | null; remindAt?: string; remindCondition?: string } = {
			starNote: starNote ? starNote : null,
		};
		if (remindAt) data.remindAt = remindAt.clone().utc().toISOString();
		// A condition only goes with a reminder (the backend rejects one without remindAt). Like
		// remindAt, leaving it out of this full-replacement PUT clears it.
		const condition = remindCondition ? remindCondition.trim() : '';
		if (remindAt && condition) data.remindCondition = condition;
		return (dispatch) =>
			new Promise<WorkOrderUserStar & { action?: string }>((resolve, reject) => {
				fetchUtils
					.put(starUrl(roleType, workOrderId), {
						headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
						data,
					})
					.then(parseJSON)
					.then((result) => {
						const status = result.status;
						const ok = result.ok;
						const json = result.json;
						if (status === 401) {
							dispatch(logoutSuccess());
						}
						if (!ok || (json && json.status === 'error')) {
							// A 400 carries a message meant for the user (e.g. "remindAt must be in the future").
							const errorMessage =
								status === 400 && json && json.message ? json.message : 'Failed to update star.';
							message.error(errorMessage);
							reject(errorMessage);
						} else {
							const saved = json && json.data !== undefined ? json.data : json;
							dispatch(
								applyWorkOrderStar(workOrderId, {
									starNote: starNote ? starNote : null,
									remindAt: nullSafeGet('remindAt', saved) || data.remindAt || null,
									remindCondition:
										nullSafeGet('remindCondition', saved) || data.remindCondition || null,
								})
							);
							dispatch(workOrderStarWritten());
							dispatch(refreshActiveReminderCount(roleType));
							resolve(saved);
						}
					})
					.catch((error) => {
						message.error('Failed to update star.');
						reject(error && error.message);
					});
			});
	};

// The user's star as the server has it now, from the work order GET (which hydrates the caller's
// stars, condition included) — search results don't carry the condition. Merged into the cache.
// Resolves the star, null once the user no longer tracks it, or undefined when the response
// carries no stars (the caller then keeps what it has).
const readWorkOrderStarForRole =
	(roleType: string) =>
	(workOrderId: number | string) =>
	(dispatch): Promise<Partial<WorkOrderUserStar> | null | undefined> =>
		fetchUtils
			.get(`${getBackendUri()}/api/v1/${roleType}/work_order/work_orders/${workOrderId}`, {
				headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
			})
			.then(parseJSON)
			.then((result) => {
				if (result.status === 401) dispatch(logoutSuccess());
				const stars = nullSafeGet('json.data.stars', result);
				if (!Array.isArray(stars)) return undefined;
				const email = currentUserEmail();
				const mine = stars.find((star) => star && compareStrings(star.email, email)) || null;
				dispatch(applyWorkOrderStar(workOrderId, mine));
				return mine;
			});

const removeWorkOrderStarForRole = (roleType: string) => (workOrderId: number | string) => {
	return (dispatch) =>
		new Promise((resolve, reject) => {
			fetchUtils
				.delete(starUrl(roleType, workOrderId), {
					headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
				})
				.then(parseJSON)
				.then((result) => {
					const status = result.status;
					const ok = result.ok;
					const json = result.json;
					if (status === 401) {
						dispatch(logoutSuccess());
					}
					if (!ok || (json && json.status === 'error')) {
						message.error('Failed to remove star.');
						reject(json && json.message ? json.message : 'Failed to remove star.');
					} else {
						dispatch(applyWorkOrderStar(workOrderId, null));
						dispatch(workOrderStarWritten());
						dispatch(refreshActiveReminderCount(roleType));
						resolve(json && json.data !== undefined ? json.data : json);
					}
				})
				.catch((error) => {
					message.error('Failed to remove star.');
					reject(error && error.message);
				});
		});
};

export const upsertWorkOrderStarForBuyer = upsertWorkOrderStarForRole(ROLE_TYPES.BUYER);
export const upsertWorkOrderStarForSupplier = upsertWorkOrderStarForRole(ROLE_TYPES.SUPPLIER);
export const removeWorkOrderStarForBuyer = removeWorkOrderStarForRole(ROLE_TYPES.BUYER);
export const removeWorkOrderStarForSupplier = removeWorkOrderStarForRole(ROLE_TYPES.SUPPLIER);

export const readWorkOrderStarForBuyer = readWorkOrderStarForRole(ROLE_TYPES.BUYER);
export const readWorkOrderStarForSupplier = readWorkOrderStarForRole(ROLE_TYPES.SUPPLIER);

export const upsertWorkOrderStar = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? upsertWorkOrderStarForSupplier : upsertWorkOrderStarForBuyer;
export const removeWorkOrderStar = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? removeWorkOrderStarForSupplier : removeWorkOrderStarForBuyer;
export const readWorkOrderStar = (userType) =>
	userType === ROLE_TYPES.SUPPLIER ? readWorkOrderStarForSupplier : readWorkOrderStarForBuyer;
